import React, { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket';
import { faArrowRotateRight } from '@fortawesome/pro-light-svg-icons/faArrowRotateRight';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { pickerMessages } from './pickerMessages';
import { PICKER_UPLOAD_ACCEPT, pickerUploadFailure } from './pickerUpload';
import { PickerUploadState } from './usePickerUpload';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './ComposerMediaPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PickerUploadBoxProps {
  state: PickerUploadState;
  onUpload: (file: File) => void;
  /** Stops the upload on the way (a video: the parts and the upload on the server). */
  onCancel: () => void;
  /** Sends the last file again after a failure. */
  onRetry: () => void;
}

/** The bar of an upload: the real share of a video, a moving bar for an image. */
const UploadProgress: React.FC<{ state: Extract<PickerUploadState, { status: 'uploading' }> }> = ({ state }) => {
  const intl = useIntl();
  let label = intl.formatMessage(pickerMessages.uploading, { name: state.fileName });
  if (state.kind === 'video') {
    label = state.checking
      ? intl.formatMessage(pickerMessages.checkingVideo, { name: state.fileName })
      : intl.formatMessage(pickerMessages.uploadingVideo, { name: state.fileName, percent: state.progress ?? 0 });
  }
  const isDeterminate = state.kind === 'video' && state.progress !== null && !state.checking;
  return (
    <>
      <span>{label}</span>
      <span className={styles.uploadTrack} aria-hidden="true">
        {isDeterminate ? (
          <span className={styles.uploadFillProgress} style={{ width: `${state.progress ?? 0}%` }} />
        ) : (
          <span className={styles.uploadFill} />
        )}
      </span>
    </>
  );
};

/**
 * "Upload from your device": the file input (images and videos), the bar while the file goes up
 * (the real byte share of a video), Stop, and the reason of a refusal or a failure with Try again.
 */
const PickerUploadBoxComponent: React.FC<PickerUploadBoxProps> = ({ state, onUpload, onCancel, onRetry }) => {
  const intl = useIntl();
  const input = useRef<HTMLInputElement>(null);
  const isUploading = state.status === 'uploading';

  const pick = useCallback(() => input.current?.click(), []);
  const onFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // The same file can be picked again after a failure.
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
      if (file !== undefined) {
        onUpload(file);
      }
    },
    [onUpload]
  );

  let error: string | null = null;
  if (state.status === 'refused') {
    if (state.reason === 'type') {
      error = intl.formatMessage(pickerMessages.refusedType, { name: state.fileName });
    } else {
      const message = state.kind === 'video' ? pickerMessages.refusedVideoSize : pickerMessages.refusedSize;
      error = intl.formatMessage(message, { name: state.fileName });
    }
  } else if (state.status === 'failed') {
    const failure = pickerUploadFailure(state.error);
    const message =
      failure === 'limit'
        ? pickerMessages.limitReached
        : failure === 'unreadable'
          ? pickerMessages.unreadable
          : pickerMessages.failed;
    error = intl.formatMessage(message, { name: state.fileName });
  }

  return (
    <div className={styles.uploadBox}>
      <input
        ref={input}
        type="file"
        accept={PICKER_UPLOAD_ACCEPT}
        className={styles.hiddenInput}
        data-testid="picker-upload-input"
        onChange={onFile}
      />
      <SMLButton icon={<FontAwesomeIcon icon={faArrowUpFromBracket} />} disabled={isUploading} onClick={pick}>
        {intl.formatMessage(pickerMessages.upload)}
      </SMLButton>
      <span className={styles.uploadHint}>{intl.formatMessage(pickerMessages.uploadHint)}</span>
      {state.status === 'uploading' && (
        <div className={styles.uploadStatus} role="status" data-testid="picker-upload-status">
          <UploadProgress state={state} />
          {state.kind === 'video' && !state.checking ? (
            <div className={styles.uploadActions}>
              <SMLButton icon={<FontAwesomeIcon icon={faXmark} />} onClick={onCancel}>
                {intl.formatMessage(pickerMessages.stop)}
              </SMLButton>
            </div>
          ) : null}
        </div>
      )}
      {error !== null && (
        <p className={styles.uploadError} role="alert">
          {error}
        </p>
      )}
      {state.status === 'failed' ? (
        <div className={styles.uploadActions}>
          <SMLButton icon={<FontAwesomeIcon icon={faArrowRotateRight} />} onClick={onRetry}>
            {intl.formatMessage(pickerMessages.retry)}
          </SMLButton>
        </div>
      ) : null}
    </div>
  );
};

export const PickerUploadBox = TypedMemo(PickerUploadBoxComponent);
